import { useEffect, useState } from "react";
import axios from "axios";
import { captureException } from "@sentry/react";
import { Box, Slider, Stack, Tooltip, Typography } from "@mui/material";
import { ZoomIn, ZoomOut } from "@mui/icons-material";
import { toast } from "react-toastify";
import { cdnDatasetImageUrl, listDatasetImageCrops } from "../api";
import { useDatasetDetail } from "./DatasetDetailContext";
import t from "../translations";

const MIN_IMAGE_SIZE = 80;
const MAX_IMAGE_SIZE = 400;
const DEFAULT_IMAGE_SIZE = 160;

interface CropImage {
  path: string;
  is_augmented: boolean;
  crop_index: number | null;
}

function parseCropImage(path: string): CropImage {
  const filename = path.split("/").pop() ?? path;
  const match = filename.match(/_(aug|original)(?:_(\d+))?\.jpe?g$/i);
  const is_augmented = match ? match[1].toLowerCase() === "aug" : false;
  const crop_index = match?.[2] ? parseInt(match[2], 10) : null;
  return { path, is_augmented, crop_index };
}

export default function CroppedImages() {
  const { datasetId, selectedImage, kind } = useDatasetDetail();
  const [crops, setCrops] = useState<CropImage[]>([]);
  const [imageSize, setImageSize] = useState(DEFAULT_IMAGE_SIZE);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setCrops([]);
    setLoaded(false);
    if (!selectedImage) return;

    const controller = new AbortController();

    async function fetchCrops() {
      try {
        const paths = await listDatasetImageCrops(
          selectedImage!.region,
          datasetId,
          selectedImage!.id,
          kind,
          controller.signal
        );
        setCrops(paths.map(parseCropImage));
        setLoaded(true);
      } catch (error) {
        if (!axios.isCancel(error)) {
          captureException(error, { event_id: "croppedImages.fetch" });
          toast.error(t.FailedToLoadImages);
        }
      }
    }

    fetchCrops();
    return () => controller.abort();
  }, [datasetId, selectedImage, kind]);

  if (!selectedImage) return null;

  return (
    <Stack
      sx={{
        width: 246,
        flexShrink: 0,
        height: "100%",
        bgcolor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
        overflow: "hidden",
      }}
      data-testid="croppedImages.container"
    >
      <Stack
        sx={{
          flexDirection: "row",
          alignItems: "center",
          px: 1.25,
          py: 0.5,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: "divider",
        }}
        data-testid="croppedImages.zoomBar"
      >
        <ZoomOut fontSize="small" sx={{ color: "rgba(255,255,255,0.6)" }} />
        <Slider
          min={MIN_IMAGE_SIZE}
          max={MAX_IMAGE_SIZE}
          value={imageSize}
          onChange={(_, v) => setImageSize(v as number)}
          size="small"
          sx={{ color: "rgba(255,255,255,0.5)", mx: 1 }}
        />
        <ZoomIn fontSize="small" sx={{ color: "rgba(255,255,255,0.6)" }} />
      </Stack>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignContent: "start",
          gap: "10px",
          flex: 1,
          overflowY: "auto",
          p: 1,
          boxSizing: "border-box",
        }}
        data-testid="croppedImages.list"
      >
        {loaded && crops.length === 0 && (
          <Typography
            sx={{
              width: "100%",
              textAlign: "center",
              color: "rgba(255,255,255,0.5)",
              m: 0,
              py: 2,
            }}
            data-testid="croppedImages.empty"
          >
            {t.NoImageCropsFound}
          </Typography>
        )}
        {crops.map(({ path, is_augmented, crop_index }) => {
          const kind = is_augmented ? t.Augmented : t.Original;
          const label = crop_index !== null ? `${kind} ${crop_index}` : kind;
          return (
            <Tooltip key={path} title={label} placement="bottom" arrow>
              <Box sx={{ width: imageSize }}>
                <Box
                  component="img"
                  src={cdnDatasetImageUrl(selectedImage.region, datasetId, path)}
                  alt={label}
                  draggable={false}
                  sx={{
                    width: "100%",
                    display: "block",
                    border: "1px solid rgba(255,255,255,0.5)",
                    borderRadius: "4px",
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Stack>
  );
}
